import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Grid, Group, Modal, ModalProps, Radio, Stack, Switch, Text} from "@mantine/core";
import {useCallback, useEffect} from "react";
import {
  CompanyEntity,
  getObjectId,
  OperatingAreaEntity,
  RouteEntity,
  RouteGroupEntity,
  SiteEntity, TagDefinitionLike,
  toRestrictionType,
  TransportRejectionType
} from "@ots-frontend/ots-data";
import {OperatingAreaSelect} from "../../select/entity/OperatingAreaSelect/OperatingAreaSelect";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {SiteSelect} from "../../select/entity/SiteSelect/SiteSelect";
import {TruckCompanySelect} from "../../select/entity/CompanySelect/TruckCompanySelect";
import {OperatorCompanySelect} from "../../select/entity/CompanySelect/OperatorCompanySelect";
import {RestrictionTypeSelect} from "../../select/entity/RestrictionTypeSelect/RestrictionTypeSelect";
import {RouteMultiSelect} from "../../select/entity/RouteSelect/RouteMultiSelect";
import {RouteGroupMultiSelect} from "../../select/entity/RouteGroupSelect/RouteGroupMultiSelect";
import {isEqual} from "lodash"
import {DateInput} from "../../date/DateInput/DateInput";

export type ContainerStatusFilterFormVariant = "default" | "dispatch";

interface ContainerStatusFilterFormProps {
  defaultValues?: Partial<ContainerStatusFilterFormValues>;
  onSubmit?: (values: ContainerStatusFilterFormValues) => void;
  formVariant?: ContainerStatusFilterFormVariant;
}

export interface ContainerStatusFilterFormValues {
  company?: CompanyEntity | string;
  operatingArea?: OperatingAreaEntity | string;
  routeGroups?: (RouteGroupEntity | string)[];
  routes?: (RouteEntity | string)[];
  site?: SiteEntity | string;
  assignedCompany?: CompanyEntity | string
  assignedOnly?: boolean
  unassignedOnly?: boolean
  transportViaPipeline?: boolean
  transportOilViaPipeline?: boolean
  usability?: "usable" | "unusable"
  restriction?: TransportRejectionType
  showDoNotUse?: boolean
  showNoData?: boolean
  highNonPrimaryVolumeOnly?: boolean
  criticalStatusOnly?: boolean
  availableLoadsOnly?: boolean
  lastDispatchedFromDate?: Date;
  lastDispatchedToDate?: Date;
  lastHauledFromDate?: Date;
  lastHauledToDate?: Date;
  neverDispatched?: boolean;
  neverHauled?: boolean;

  tagDefinitions?: TagDefinitionLike[]
}

export const DEFAULT_CONTAINER_STATUS_FILTER: ContainerStatusFilterFormValues = {
  company: undefined,
  operatingArea: undefined,
  routeGroups: undefined,
  routes: undefined,
  site: undefined,
  assignedCompany: undefined,
  unassignedOnly: false,
  transportViaPipeline: undefined,
  transportOilViaPipeline: undefined,
  usability: undefined,
  restriction: undefined,
  showDoNotUse: true,
  showNoData: true,
  highNonPrimaryVolumeOnly: false,
  criticalStatusOnly: false,
  availableLoadsOnly: false,
  lastDispatchedFromDate: undefined,
  lastDispatchedToDate: undefined,
  lastHauledFromDate: undefined,
  lastHauledToDate: undefined,
  neverDispatched: false,
  neverHauled: false,
  tagDefinitions: undefined,
}

export const DEFAULT_DISPATCH_GO_CONTAINER_STATUS_FILTER: ContainerStatusFilterFormValues = {
  ...DEFAULT_CONTAINER_STATUS_FILTER,
  assignedOnly: true,
  assignedCompany: undefined,
  unassignedOnly: false,
}

export const isDefaultFilter = (
  filter1: ContainerStatusFilterFormValues,
  defaultFilter: ContainerStatusFilterFormValues = DEFAULT_CONTAINER_STATUS_FILTER,
): boolean => {
  if (filter1 === defaultFilter) {
    return true
  }

  if (getObjectId(filter1.company) !== getObjectId(defaultFilter.company)) {
    return false;
  }

  const routeGroupIds = filter1.routeGroups?.map(getObjectId)
  const defaultRouteGroupIds = defaultFilter.routeGroups?.map(getObjectId)
  if (!isEqual(routeGroupIds, defaultRouteGroupIds)) {
    return false;
  }

  if (getObjectId(filter1.operatingArea) !== getObjectId(defaultFilter.operatingArea)) {
    return false;
  }

  const routeIds = filter1.routes?.map(getObjectId)
  const defaultRouteIds = defaultFilter.routes?.map(getObjectId)
  if (!isEqual(routeIds, defaultRouteIds)) {
    return false;
  }

  if (getObjectId(filter1.site) !== getObjectId(defaultFilter.site)) {
    return false;
  }

  if (getObjectId(filter1.assignedCompany) !== getObjectId(defaultFilter.assignedCompany)) {
    return false;
  }

  if (filter1.transportViaPipeline !== defaultFilter.transportViaPipeline) {
    return false
  }

  if (filter1.transportOilViaPipeline !== defaultFilter.transportOilViaPipeline) {
    return false
  }

  if (filter1.usability !== defaultFilter.usability) {
    return false
  }

  if (filter1.restriction?.code !== defaultFilter.restriction?.code) {
    return false
  }

  if (filter1.unassignedOnly !== defaultFilter.unassignedOnly) {
    return false
  }

  if (filter1.assignedOnly !== defaultFilter.assignedOnly) {
    return false
  }

  if (filter1.showDoNotUse !== defaultFilter.showDoNotUse) {
    return false
  }

  if (filter1.showNoData !== defaultFilter.showNoData) {
    return false
  }

  if (filter1.highNonPrimaryVolumeOnly !== defaultFilter.highNonPrimaryVolumeOnly) {
    return false
  }

  if (filter1.criticalStatusOnly !== defaultFilter.criticalStatusOnly) {
    return false
  }

  if (filter1.availableLoadsOnly !== defaultFilter.availableLoadsOnly) {
    return false
  }

  if (filter1.lastDispatchedFromDate?.getTime() !== defaultFilter.lastDispatchedFromDate?.getTime()) {
    return false;
  }

  if (filter1.lastDispatchedToDate?.getTime() !== defaultFilter.lastDispatchedToDate?.getTime()) {
    return false;
  }

  if (filter1.lastHauledFromDate?.getTime() !== defaultFilter.lastHauledFromDate?.getTime()) {
    return false;
  }

  if (filter1.lastHauledToDate?.getTime() !== defaultFilter.lastHauledToDate?.getTime()) {
    return false;
  }

  if (filter1.neverDispatched !== defaultFilter.neverDispatched) {
    return false;
  }

  if (filter1.neverHauled !== defaultFilter.neverHauled) {
    return false;
  }

  const tagDefinitionIds = filter1.tagDefinitions?.map(def => def.id) ?? []
  const defaultTagDefinitionIds = defaultFilter.tagDefinitions?.map(def => def.id) ?? []
  if (!isEqual(tagDefinitionIds, defaultTagDefinitionIds)) {
    return false;
  }

  return true;
}

export const ContainerStatusFilterForm = (props: ContainerStatusFilterFormProps) => {
  const {
    defaultValues,
    onSubmit,
    formVariant = "default",
  } = props;

  const dispatchVariant = formVariant === "dispatch";

  const {t} = useTranslation(["common"]);

  const {regionId} = useApp()

  const form = useForm({
    initialValues: {
      company: getObjectId(defaultValues?.company),
      site: getObjectId(defaultValues?.site),
      operatingArea: getObjectId(defaultValues?.operatingArea),
      routeGroups: defaultValues?.routeGroups?.map(getObjectId),
      routes: defaultValues?.routes?.map(getObjectId),
      assignedCompany: getObjectId(defaultValues?.assignedCompany),
      assignedOnly: defaultValues?.assignedOnly ?? (dispatchVariant ? true : undefined),
      unassignedOnly: defaultValues?.unassignedOnly ?? false,
      transportViaPipeline: typeof defaultValues?.transportViaPipeline === "undefined" ? "all" : (defaultValues.transportViaPipeline ? "yes" : "no"),
      transportOilViaPipeline: typeof defaultValues?.transportOilViaPipeline === "undefined" ? "all" : (defaultValues.transportOilViaPipeline ? "yes" : "no"),
      usability: defaultValues?.usability ?? "all",
      restriction: defaultValues?.restriction?.code,
      showDoNotUse: defaultValues?.showDoNotUse ?? true,
      showNoData: defaultValues?.showNoData ?? true,
      highNonPrimaryVolumeOnly: defaultValues?.highNonPrimaryVolumeOnly ?? false,
      criticalStatusOnly: defaultValues?.criticalStatusOnly ?? false,
      availableLoadsOnly: defaultValues?.availableLoadsOnly ?? false,
      lastDispatchedFromDate: defaultValues?.lastDispatchedFromDate,
      lastDispatchedToDate: defaultValues?.lastDispatchedToDate,
      lastHauledFromDate: defaultValues?.lastHauledFromDate,
      lastHauledToDate: defaultValues?.lastHauledToDate,
      neverDispatched: defaultValues?.neverDispatched ?? false,
      neverHauled: defaultValues?.neverHauled ?? false,
    },
    validate: {
      company: (value, values) => dispatchVariant && !values.assignedOnly && !value
        ? t<string>("common.form.validation.required", "Company is required")
        : undefined,
      lastDispatchedFromDate: (value, values) => {
        let error: string | undefined = undefined
        if (value && values.lastDispatchedToDate && value.getTime() > values.lastDispatchedToDate.getTime()) {
          error = t<string>("common.form.validation.todatebeforefrom", "The to date can't be before the from date")
        }
        return error
      },
      lastHauledFromDate: (value, values) => {
        let error: string | undefined = undefined
        if (value && values.lastHauledToDate && value.getTime() < values.lastHauledToDate.getTime()) {
          error = t<string>("common.form.validation.todatebeforefrom", "The to date can't be before the from date")
        }
        return error
      },
    }
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        company: values.company,
        operatingArea: values.operatingArea,
        routeGroups: values.routeGroups,
        routes: values.routes,
        site: values.site,
        assignedCompany: dispatchVariant ? undefined : values.assignedCompany,
        assignedOnly: dispatchVariant ? values.assignedOnly : undefined,
        unassignedOnly: dispatchVariant ? false : values.unassignedOnly,
        transportViaPipeline: values.transportViaPipeline === 'all' ? undefined : (values.transportViaPipeline === "yes"),
        transportOilViaPipeline: values.transportOilViaPipeline === 'all' ? undefined : (values.transportOilViaPipeline === "yes"),
        usability: values.usability === 'all' ? undefined : (values.usability === "usable" || values.usability === "unusable") ? values.usability : undefined,
        restriction: values.restriction ? toRestrictionType(values.restriction) : undefined,
        showDoNotUse: values.showDoNotUse,
        showNoData: values.showNoData,
        highNonPrimaryVolumeOnly: values.highNonPrimaryVolumeOnly,
        criticalStatusOnly: values.criticalStatusOnly,
        availableLoadsOnly: values.availableLoadsOnly,
        lastDispatchedFromDate: values.lastDispatchedFromDate,
        lastDispatchedToDate: values.lastDispatchedToDate,
        lastHauledFromDate: values.lastHauledFromDate,
        lastHauledToDate: values.lastHauledToDate,
        neverDispatched: values.neverDispatched,
        neverHauled: values.neverHauled,
        tagDefinitions: defaultValues?.tagDefinitions,
      });
    }
  }, [defaultValues?.tagDefinitions, dispatchVariant, form, onSubmit]);

  const onClearClick = useCallback(() => {
    if (onSubmit) {
      onSubmit(dispatchVariant ? DEFAULT_DISPATCH_GO_CONTAINER_STATUS_FILTER : DEFAULT_CONTAINER_STATUS_FILTER)
    }
  }, [dispatchVariant, onSubmit])

  useEffect(() => {
    const unassignedOnly = form.getTransformedValues().unassignedOnly
    const assignedCompanyId = form.getTransformedValues().assignedCompany
    if (unassignedOnly && assignedCompanyId) {
      form.setFieldValue('assignedCompany', undefined)
    }
  }, [form])

  useEffect(() => {
    if (form.values.neverDispatched) {
      if (form.values.lastDispatchedFromDate) {
        form.setFieldValue("lastDispatchedFromDate", null);
      }
      if (form.values.lastDispatchedToDate) {
        form.setFieldValue("lastDispatchedToDate", null);
      }
    }
    if (form.values.neverHauled) {
      if (form.values.lastHauledFromDate) {
        form.setFieldValue("lastHauledFromDate", null);
      }
      if (form.values.lastHauledToDate) {
        form.setFieldValue("lastHauledToDate", null);
      }
    }
  }, [
    form,
    form.values.neverDispatched,
    form.values.neverHauled,
  ]);


  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Stack spacing="md">
        {dispatchVariant &&
          <Switch
            label={t("common.entity.containerstatus.filter.form.field.assignedonly", "Assigned Only")}
            description={form.values.assignedOnly
              ? t("common.entity.containerstatus.filter.form.field.assignedonly.description", "Show containers assigned to your company")
              : t("common.entity.containerstatus.filter.form.field.assignedonly.off.description", "Show all containers for the selected company")}
            {...form.getInputProps("assignedOnly", {type: "checkbox"})}
          />
        }
        <OperatorCompanySelect
          label={t("common.entity.containerstatus.filter.form.field.company", "Company")}
          placeholder={t<string>("common.entity.containerstatus.filter.form.field.company", "Company")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          withAsterisk={dispatchVariant && !form.values.assignedOnly}
          clearable
          {...form.getInputProps("company")} />
        <OperatingAreaSelect
          label={t("common.entity.containerstatus.filter.form.field.operatingarea", "Operating Area")}
          placeholder={t<string>("common.entity.containerstatus.filter.form.field.operatingarea", "Operating Area")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("operatingArea")} />
        <RouteGroupMultiSelect
          label={t("common.entity.containerstatus.filter.form.field.routegroup", "Route Group")}
          placeholder={t<string>("common.entity.containerstatus.filter.form.field.routegroup", "Route Group")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("routeGroups")} />
        <RouteMultiSelect
          label={t("common.entity.containerstatus.filter.form.field.route", "Route")}
          placeholder={t<string>("common.entity.containerstatus.filter.form.field.route", "Route")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("routes")} />
        <SiteSelect
          label={t("common.entity.containerstatus.filter.form.field.site", "Site")}
          placeholder={t<string>("common.entity.containerstatus.filter.form.field.site", "Site")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("site")} />
        {!dispatchVariant &&
          <>
            <Divider my="sm" variant="dotted"/>
            <TruckCompanySelect
              label={t("common.entity.containerstatus.filter.form.field.assignedcompany", "Assigned Hauler")}
              placeholder={t<string>("common.entity.containerstatus.filter.form.field.assignedcompany", "Assigned Hauler")}
              selector={{
                regionId: regionId,
              }}
              withinPortal={true}
              disabled={form.getTransformedValues().unassignedOnly}
              clearable
              {...form.getInputProps("assignedCompany")} />
            <Switch
              label={t("common.entity.containerstatus.filter.form.field.unassignedonly", "Unassigned Only")}
              placeholder={t<string>("common.entity.containerstatus.filter.form.field.unassignedonly", "Unassigned Only")}
              {...form.getInputProps("unassignedOnly", {type: 'checkbox'})} />
          </>
        }
        <Divider my="sm" variant="dotted"/>
        <Radio.Group
          label={t("common.entity.containerstatus.filter.form.field.usability", "Usability")}
          {...form.getInputProps("usability")}
        >
          <Group mt="xs">
            <Radio value="all" label={t("common.entity.containerstatus.filter.form.field.usability.all", "All")}/>
            <Radio value="usable"
                   label={t("common.entity.containerstatus.filter.form.field.usability.usable", "Usable")}/>
            <Radio value="delivery"
                   label={t("common.entity.containerstatus.filter.form.field.usability.unusable", "Unusable")}/>
          </Group>
        </Radio.Group>
        <Divider my="sm" variant="dotted"/>
        <RestrictionTypeSelect
          label={t("common.entity.containerstatus.filter.form.field.restriction", "Restriction")}
          placeholder={t<string>("common.entity.containerstatus.filter.form.field.restriction", "Restriction")}
          clearable={true}
          {...form.getInputProps("restriction")}
        />
        <Divider my="sm" variant="dotted"/>
        <Stack spacing="xs">
          <Text size="sm" weight={600}>
            {t("common.entity.containerstatus.filter.form.section.transport", "Transport")}
          </Text>
          <Radio.Group
            label={t("common.entity.containerstatus.filter.form.field.transportviapipline", "Transport Water Via Pipeline")}
            {...form.getInputProps("transportViaPipeline")}
          >
            <Group mt="xs">
              <Radio value="all" label={t("common.shared.all", "All")}/>
              <Radio value="yes" label={t("common.shared.true", "True")}/>
              <Radio value="no" label={t("common.shared.false", "False")}/>
            </Group>
          </Radio.Group>
          <Radio.Group
            label={t("common.entity.containerstatus.filter.form.field.transportoilviapipline", "Transport Oil Via Pipeline")}
            {...form.getInputProps("transportOilViaPipeline")}
          >
            <Group mt="xs">
              <Radio value="all" label={t("common.shared.all", "All")}/>
              <Radio value="yes" label={t("common.shared.true", "True")}/>
              <Radio value="no" label={t("common.shared.false", "False")}/>
            </Group>
          </Radio.Group>
        </Stack>
        <Divider my="sm" variant="dotted"/>
        <Stack spacing="xs">
          <Text size="sm" weight={600}>
            {t("common.entity.containerstatus.filter.form.section.activity", "Activity")}
          </Text>

          <Grid gutter="sm">
            <Grid.Col span={6}>
              <DateInput
                label={t("common.entity.containerstatus.filter.form.field.lastdispatchedfrom", "Last Dispatched From")}
                placeholder={t<string>("common.shared.selectDate", "Select date")}
                valueFormat="MM/DD/YYYY HH:mm"
                clearable
                disabled={form.values.neverDispatched}
                {...form.getInputProps("lastDispatchedFromDate")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <DateInput
                label={t("common.entity.containerstatus.filter.form.field.lastdispatchedto", "Last Dispatched To")}
                placeholder={t<string>("common.shared.selectDate", "Select date")}
                valueFormat="MM/DD/YYYY HH:mm"
                clearable
                disabled={form.values.neverDispatched}
                {...form.getInputProps("lastDispatchedToDate")}
              />
            </Grid.Col>

            <Grid.Col span={6}>
              <DateInput
                label={t("common.entity.containerstatus.filter.form.field.lasthauledfrom", "Last Hauled From")}
                placeholder={t<string>("common.shared.selectDate", "Select date")}
                valueFormat="MM/DD/YYYY HH:mm"
                clearable
                disabled={form.values.neverHauled}
                {...form.getInputProps("lastHauledFromDate")}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <DateInput
                label={t("common.entity.containerstatus.filter.form.field.lasthauledto", "Last Hauled To")}
                placeholder={t<string>("common.shared.selectDate", "Select date")}
                valueFormat="MM/DD/YYYY HH:mm"
                clearable
                disabled={form.values.neverHauled}
                {...form.getInputProps("lastHauledToDate")}
              />
            </Grid.Col>
          </Grid>

          <Group grow>
            <Switch
              label={t("common.entity.containerstatus.filter.form.field.neverdispatched", "Never Dispatched")}
              {...form.getInputProps("neverDispatched", {type: "checkbox"})}
            />
            <Switch
              label={t("common.entity.containerstatus.filter.form.field.neverhauled", "Never Hauled")}
              {...form.getInputProps("neverHauled", {type: "checkbox"})}
            />
          </Group>
        </Stack>
        <Divider my="sm" variant="dotted"/>
        <Switch
          label={t("common.entity.containerstatus.filter.form.field.showdonotuse", "Show Do Not Use")}
          placeholder={t<string>("common.entity.containerstatus.filter.form.field.showdonotuse", "Show Do Not Use")}
          {...form.getInputProps("showDoNotUse", {type: 'checkbox'})} />
        <Switch
          label={t("common.entity.containerstatus.filter.form.field.shownodata", "Show No Data")}
          placeholder={t<string>("common.entity.containerstatus.filter.form.field.shownodata", "Show No Data")}
          {...form.getInputProps("showNoData", {type: 'checkbox'})} />
        <Switch
          label={t("common.entity.containerstatus.filter.form.field.highnonprimaryvolumeonly", "High Non-Primary Volume Only")}
          placeholder={t<string>("common.entity.containerstatus.filter.form.field.highnonprimaryvolumeonly", "High Non-Primary Volume Only")}
          {...form.getInputProps("highNonPrimaryVolumeOnly", {type: 'checkbox'})} />
        <Switch
          label={t("common.entity.containerstatus.filter.form.field.criticalstatusonly", "Critical Levels Only")}
          placeholder={t<string>("common.entity.containerstatus.filter.form.field.criticalstatusonly", "Critical Levels Only")}
          {...form.getInputProps("criticalStatusOnly", {type: 'checkbox'})} />
        <Switch
          label={t("common.entity.containerstatus.filter.form.field.availableloadsonly", "Available Loads Only")}
          placeholder={t<string>("common.entity.containerstatus.filter.form.field.availableloadsonly", "Available Loads Only")}
          {...form.getInputProps("availableLoadsOnly", {type: 'checkbox'})} />
      </Stack>
      <Group position="center" grow={true}>
        <Button fullWidth mt="xl" onClick={onClearClick}>
          {t("common.action.clear", "Clear", {ns: "common"})}
        </Button>
        <Button type="submit" fullWidth mt="xl">
          {t("common.action.submit", "Submit", {ns: "common"})}
        </Button>
      </Group>
    </form>
  );
};

interface ContainerStatusFilterFormModalProps extends ContainerStatusFilterFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const ContainerStatusFilterFormModal = (props: ContainerStatusFilterFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    formVariant,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <ContainerStatusFilterForm defaultValues={defaultValues} onSubmit={onSubmit} formVariant={formVariant}/>
    </Modal>
  );
};
